import { useCallback, useRef, useState } from "react";

/**
 * useCellHighlight — der VERERBBARE Highlight-Mechanismus für die Detail-Views
 * (2026-07-13, Thomas: „mach es vererbbar, nicht pro View nachziehen").
 *
 * Eine Detail-View hat zwei Seiten: die QUELLE (`source`, links — Scan-Overlay
 * oder eingelesene Roh-Zeilen) und den WERT (`value`, rechts — Summen/Berechnung).
 * Beide Seiten sind bidirektional verlinkt:
 *   - Hover einer Zelle setzt `active` (die View entscheidet per Prädikat, welche
 *     Gegenstücke sie einfärbt — die fachliche Zuordnung bleibt in der View).
 *   - KLICK springt zum Gegenstück: das erste passende Element auf der anderen
 *     Seite wird zentriert in den sichtbaren Bereich gescrollt UND kurz gepulst.
 *     Das löst den Excel-Fall, in dem die Quell-Zeilen weit unten / außerhalb
 *     des Sichtbereichs liegen (Thomas' zweite Beschwerde).
 *
 * Generisch über den Key-Typ `K` (nur für `active`/`setActive` genutzt), damit
 * derselbe Hook für PdfWithHighlights (day/field), ZulagenblattHighlights
 * (Spalten-Key) und den scanlosen Excel-Fall (Gruppe ↔ Roh-Zeilen-Index) passt.
 *
 * Das Verlinken selbst läuft DOM-basiert über `data-hl-side` + `data-hl-match`:
 * ein `source`-Element mit Match-Tokens [a,b] ist mit jedem `value`-Element
 * verbunden, dessen Match-Token in {a,b} liegt — und umgekehrt. Damit trägt die
 * View nur die Tokens ein; die Traversierung (finden/scrollen/pulsen) ist hier
 * einmal implementiert.
 */

export type HlSide = "source" | "value";

const OTHER: Record<HlSide, HlSide> = { source: "value", value: "source" };

/** Kurzer Aufmerksamkeits-Puls via Web Animations API — überlebt React-Re-Renders
 *  (verändert weder className noch Inline-Style dauerhaft) und fällt sauber auf
 *  den Ruhezustand des Elements zurück. In jsdom (Tests) ist `animate` evtl.
 *  nicht vorhanden → optional-chained no-op. */
function pulse(el: Element): void {
  (el as HTMLElement).animate?.(
    [
      { boxShadow: "0 0 0 0 rgba(245,158,11,0.0)", backgroundColor: "rgba(245,158,11,0.55)" },
      { boxShadow: "0 0 0 6px rgba(245,158,11,0.0)", backgroundColor: "rgba(245,158,11,0.0)" },
    ],
    { duration: 1100, easing: "ease-out" },
  );
}

/** Props, die eine Zelle interaktiv machen (Spread auf das Element). Ohne
 *  `style` — der Aufrufer mergt `cursor` selbst in sein bestehendes Inline-Style,
 *  damit kein vorhandenes `style`-Attribut überschrieben wird. */
export interface HlLinkProps {
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onClick?: (e: React.MouseEvent) => void;
  "data-hl-side": HlSide;
  "data-hl-match": string;
}

export interface CellHighlightApi<K> {
  active: K | null;
  setActive: (k: K | null) => void;
  /** An den gemeinsamen Vorfahren BEIDER Panels hängen — der Bereich, in dem
   *  „zum Gegenstück springen" nach Elementen sucht (DetailViewShell setzt das
   *  auf die Panel-Wrapper, wenn `highlight` übergeben wird). */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Das erste Gegenstück auf `side`, dessen Match-Tokens `tokens` schneiden,
   *  zentriert einscrollen und alle Treffer pulsen. */
  jumpTo: (side: HlSide, tokens: string[]) => void;
  /** Event-/Data-Props für eine highlightbare Zelle. Hover treibt `active`
   *  (über `key`); Klick springt zur Gegenseite über `match`-Tokens. */
  linkProps: (opts: {
    side: HlSide;
    key: K;
    /** Verbindungs-Tokens zur Gegenseite (Default: [String(key)]). */
    match?: string | string[];
    /** Default true — false für Zellen, die hoveren/highlighten, aber beim Klick
     *  nicht springen sollen. */
    jump?: boolean;
  }) => HlLinkProps;
}

function toTokens(match: string | string[] | undefined, key: unknown): string[] {
  if (Array.isArray(match)) return match.filter(Boolean);
  if (typeof match === "string") return match ? [match] : [];
  return [String(key)];
}

export function useCellHighlight<K = string>(): CellHighlightApi<K> {
  const [active, setActive] = useState<K | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const jumpTo = useCallback((side: HlSide, tokens: string[]) => {
    const root = containerRef.current;
    if (!root || tokens.length === 0) return;
    const want = new Set(tokens);
    const matches: HTMLElement[] = [];
    root.querySelectorAll<HTMLElement>(`[data-hl-side="${side}"]`).forEach((el) => {
      const elTokens = (el.getAttribute("data-hl-match") ?? "").split(" ").filter(Boolean);
      if (elTokens.some((t) => want.has(t))) matches.push(el);
    });
    if (matches.length === 0) return;
    // Erstes Gegenstück zentrieren (jsdom kennt scrollIntoView evtl. nicht) …
    matches[0]!.scrollIntoView?.({ block: "center", behavior: "smooth" });
    // … und ALLE Treffer pulsen (eine Gruppe hat i.d.R. mehrere Quell-Zeilen).
    for (const el of matches) pulse(el);
  }, []);

  const linkProps = useCallback<CellHighlightApi<K>["linkProps"]>(
    ({ side, key, match, jump = true }) => {
      const tokens = toTokens(match, key);
      return {
        onPointerEnter: () => setActive(key),
        onPointerLeave: () => setActive(null),
        onClick: jump ? () => jumpTo(OTHER[side], tokens) : undefined,
        "data-hl-side": side,
        "data-hl-match": tokens.join(" "),
      };
    },
    [jumpTo],
  );

  return { active, setActive, containerRef, jumpTo, linkProps };
}
